import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { I18nProvider } from "@/contexts/I18nContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { ToastStack } from "@/components/Toast";
import { AuthProvider } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoginPage } from "@/pages/LoginPage";
import { AuthActionPage } from "@/pages/AuthActionPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { LegalGate } from "@/features/legal/LegalGate";
import { InstitutionSettingsPage } from "@/features/institution/InstitutionSettingsPage";
import { LegalPage } from "@/features/legal/LegalPage";
import { ConsentPage } from "@/features/consent/ConsentPage";
import { PeoplePage } from "@/features/people/PeoplePage";
import { PersonPage } from "@/features/people/PersonPage";
import { NewPersonPage } from "@/features/people/NewPersonPage";
import { ImportWizard } from "@/features/people/import/ImportWizard";
import { DevicesPage } from "@/features/devices/DevicesPage";
import { AddDevicesPage } from "@/features/devices/AddDevicesPage";
import { KitsPage } from "@/features/kits/KitsPage";
import { GroupsPage } from "@/features/groups/GroupsPage";
import { NewGroupPage } from "@/features/groups/NewGroupPage";
import { GroupPage } from "@/features/groups/GroupPage";
import { SessionsPage } from "@/features/sessions/SessionsPage";
import { SessionReviewPage } from "@/features/sessions/SessionReviewPage";
import { ReportsPage } from "@/features/reports/ReportsPage";
import { BillingPage } from "@/features/billing/BillingPage";
import { SupportPage } from "@/features/support/SupportPage";
import { COMPOSER_URL } from "@/lib/links";
import type { UserRole } from "@/types";

/** A route that leaves the app entirely. */
function ExternalRedirect({ to }: { to: string }) {
  window.location.replace(to);
  return null;
}

/**
 * Routes.
 *
 * The provider order is not arbitrary. `I18nProvider` is outermost because the
 * login screen has to be readable before anyone knows who is reading it;
 * `ToastProvider` next because `AuthProvider` can fail and needs somewhere to
 * say so; `AuthProvider` innermost because it sets the locale from the profile
 * once it knows one.
 *
 * Every route is inside `ProtectedRoute` except `/login`. Sections whose bodies
 * land in later phases are registered now with their heading only, so the
 * navigation, the role guard and the 403 page are all real from the first day
 * rather than being wired up at the end.
 */

const STAFF: readonly UserRole[] = ["owner", "teacher"];
const OWNER: readonly UserRole[] = ["owner"];

function guarded(element: JSX.Element, roles?: readonly UserRole[]) {
  return <ProtectedRoute roles={roles}>{element}</ProtectedRoute>;
}

export function App() {
  return (
    <I18nProvider>
      <ToastProvider>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/action" element={<AuthActionPage />} />
              <Route path="/change-password" element={guarded(<ChangePasswordPage />)} />

              <Route
                element={
                  <ProtectedRoute roles={STAFF}>
                    {/*
                      Inside the guard and outside the layout: an owner who owes
                      an acceptance is stopped before they see a navigation they
                      cannot usefully act on.
                    */}
                    <LegalGate>
                      <Layout />
                    </LegalGate>
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="people" element={guarded(<PeoplePage />, OWNER)} />
                <Route path="people/new" element={guarded(<NewPersonPage />, OWNER)} />
                <Route path="people/import" element={guarded(<ImportWizard />, OWNER)} />
                <Route path="people/:uid" element={guarded(<PersonPage />, OWNER)} />
                <Route path="devices" element={guarded(<DevicesPage />, OWNER)} />
                <Route path="devices/add" element={guarded(<AddDevicesPage />, OWNER)} />
                <Route path="kits" element={<KitsPage />} />
                <Route path="groups" element={<GroupsPage />} />
                <Route path="groups/new" element={<NewGroupPage />} />
                <Route path="groups/:groupId" element={<GroupPage />} />
                {/* The library moved to Composer; bookmarks follow it. */}
                <Route
                  path="library/*"
                  element={<ExternalRedirect to={`${COMPOSER_URL}/mine`} />}
                />
                <Route path="sessions" element={<SessionsPage />} />
                <Route path="sessions/:sessionId" element={<SessionReviewPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="institution" element={guarded(<InstitutionSettingsPage />, OWNER)} />
                <Route path="consent" element={<ConsentPage />} />
                <Route path="billing" element={guarded(<BillingPage />, OWNER)} />
                <Route path="support" element={guarded(<SupportPage />, OWNER)} />
                <Route path="legal" element={<LegalPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
        {/*
          Outside the router, and outside the layout. Notifications belong to
          the application, not to a page: the first version rendered this inside
          `Layout`, which meant a failed sign-in — the one error a person is
          most likely to hit — produced nothing at all on screen.
        */}
        <ToastStack />
      </ToastProvider>
    </I18nProvider>
  );
}
